(function () {
  'use strict';

  class CornerFitCartEnhancer extends HTMLElement {
    connectedCallback() {
      this.drawer = document.querySelector('cart-drawer');
      if (!this.drawer) return;
      this.refresh = this.refresh.bind(this);
      this.observer = new MutationObserver(() => {
        if (this.isInjecting) return;
        window.clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(this.refresh, 120);
      });
      this.observer.observe(this.drawer, { childList: true, subtree: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.clearTimeout(this.refreshTimer);
      this.controller?.abort();
    }

    async refresh() {
      if (!this.drawer || this.fetching) return;
      this.fetching = true;
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const base = window.routes?.cart_url || '/cart';
        const response = await fetch(`${base}.js`, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        this.inject(cart);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('Corner Fit cart enhancement could not refresh.', error);
      } finally {
        this.fetching = false;
      }
    }

    inject(cart) {
      this.isInjecting = true;
      this.drawer.querySelectorAll('[data-cf-cart-enhanced]').forEach((node) => node.remove());

      if (this.dataset.showProgress === 'true') {
        const header = this.drawer.querySelector('.drawer__header');
        if (header) header.insertAdjacentElement('afterend', this.buildProgress(cart));
      }

      const footer = this.drawer.querySelector('.drawer__footer');
      const ctas = footer?.querySelector('.cart__ctas');
      if (ctas) {
        const savings = Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
        if (this.dataset.showSavings === 'true' && savings > 0) {
          const row = document.createElement('div');
          row.className = 'cf-cart-savings';
          row.dataset.cfCartEnhanced = '';
          row.innerHTML = '<span></span><strong></strong>';
          row.querySelector('span').textContent = this.dataset.savingsLabel || 'You save';
          row.querySelector('strong').textContent = this.money(savings, cart.currency);
          ctas.insertAdjacentElement('beforebegin', row);
        }
        const template = this.querySelector('template[data-cf-cart-footer-template]');
        if (template) {
          const wrapper = document.createElement('div');
          wrapper.dataset.cfCartEnhanced = '';
          wrapper.append(template.content.cloneNode(true));
          ctas.insertAdjacentElement('afterend', wrapper);
        }
      }

      requestAnimationFrame(() => { this.isInjecting = false; });
    }

    buildProgress(cart) {
      const goal = Math.max(1, Number(this.dataset.goalCents || 5000));
      const remaining = Math.max(0, goal - Number(cart.total_price || 0));
      const percent = Math.min(100, Math.round(Number(cart.total_price || 0) * 100 / goal));
      const message = remaining > 0
        ? (this.dataset.beforeMessage || "You're [amount] away from FREE shipping").replace('[amount]', this.money(remaining, cart.currency))
        : (this.dataset.afterMessage || 'You unlocked FREE shipping!');
      const node = document.createElement('div');
      node.className = 'cf-cart-progress';
      node.dataset.cfCartEnhanced = '';
      node.setAttribute('aria-label', message);
      node.innerHTML = `
        <p class="cf-cart-progress__message"></p>
        <div class="cf-cart-progress__track" style="--cf-cart-progress:${percent}%;--cf-cart-progress-color:${this.dataset.progressColor};--cf-cart-progress-background:${this.dataset.progressBackground};">
          <span class="cf-cart-progress__bar"></span>
        </div>`;
      node.querySelector('.cf-cart-progress__message').textContent = message;
      return node;
    }

    money(cents, currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currency || window.Shopify?.currency?.active || 'USD',
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }
  }

  if (!customElements.get('cf-cart-enhancer')) {
    customElements.define('cf-cart-enhancer', CornerFitCartEnhancer);
  }
})();
